import type { EntityCellValue, FactValue } from "../../Dashboard/types";

// Whether a cell value should count as empty. Mirrors the Facts-tab
// `isEmptyScalarLike` / `computeIsEmpty` helpers in
// EditableFactRenderer (issue #4914): arrays are empty when length
// is 0; scalars are empty when both `scalar` is nullish /
// blank-string / blank-formatted-address AND `entity` is null.
//
// Lives outside the orchestrator so per-cell renderers (FactCell)
// can use the same predicate to decide whether the autofill
// failure tint should still apply.

const isEmptyScalarRaw = (raw: unknown): boolean => {
  if (raw === null || raw === undefined) return true;
  if (typeof raw === "string" && raw.trim() === "") return true;
  // An empty array stored as a scalar is an absent value. This lives HERE,
  // not as a carve-out in a caller: this predicate is the one definition of
  // "held", and a caller-side exception made the Facts legend disagree with
  // the badge over exactly this shape (#11426 round-4 review).
  if (Array.isArray(raw) && raw.length === 0) return true;
  // Address shells store empty as { formatted: "", validated: false }.
  if (
    typeof raw === "object" &&
    raw !== null &&
    "formatted" in raw &&
    typeof (raw as { formatted: unknown }).formatted === "string" &&
    (raw as { formatted: string }).formatted.trim() === ""
  ) {
    return true;
  }
  return false;
};

export const isCellValueEmpty = (
  value: EntityCellValue | null | undefined
): boolean => {
  if (value === undefined || value === null) return true;
  if (Array.isArray(value)) return value.length === 0;
  const fv = value as FactValue;
  return isEmptyScalarRaw(fv.scalar) && fv.entity === null;
};
